// Pure, framework-free helpers for the retention-offer-limit gating logic
// used by the Retention icon on the customer agreement history page. Kept
// in sync with the equivalent modules in racpad_agreement and
// racpad_payment1 since the business rules are identical across all
// retention-icon surfaces.

// Backend now returns the raw "agreementActivities" ref_code array (the
// retention-offer activity types recorded across the agreement's exchange
// chain) instead of a pre-computed count; derive the count client-side.
//
// es_agreementpayment (the only BE source that feeds this page's PastDue
// data) returns agreementActivities: null specifically for club-membership
// rows in that response, since retention offers don't apply to club at all
// (as opposed to []: a real agreement with zero retention offers applied).
// This function intentionally treats null the same as [] -> count 0, which
// is safe here because the Retention icon is independently gated on
// `contractType !== 'CLUB'` at the render site (see AgreementInfo.tsx), so
// club rows never reach a point where this count is used to show the icon.
export const getRetentionOfferCount = (
  agreementActivities: string[] | null | undefined
): number => {
  return Array.isArray(agreementActivities) ? agreementActivities.length : 0;
};

export interface RetentionOfferLimitConfig {
  retentionOfferLimitEnabled: boolean;
  maxRetentionOffersPerAgreement: number;
}

export interface RetentionOfferLimitState extends RetentionOfferLimitConfig {
  retentionOfferCount: number;
}

// Icon should not render at all when the store has the limit feature enabled
// but has configured a max of 0 (i.e. retention offers are fully disabled).
export const isRetentionIconHidden = ({
  retentionOfferLimitEnabled,
  maxRetentionOffersPerAgreement,
}: RetentionOfferLimitConfig): boolean => {
  return retentionOfferLimitEnabled && maxRetentionOffersPerAgreement === 0;
};

// Icon should render but be disabled (blurred/no-op click) once the
// agreement's existing retention-offer count has reached the configured max.
export const isRetentionLimitReached = ({
  retentionOfferLimitEnabled,
  maxRetentionOffersPerAgreement,
  retentionOfferCount,
}: RetentionOfferLimitState): boolean => {
  return (
    retentionOfferLimitEnabled &&
    maxRetentionOffersPerAgreement > 0 &&
    retentionOfferCount >= maxRetentionOffersPerAgreement
  );
};

export const getRetentionLimitTooltip = (
  maxRetentionOffersPerAgreement: number
): string => {
  return maxRetentionOffersPerAgreement === 1
    ? 'Retention offer has already been applied to this agreement. Only 1 offer is allowed per agreement.'
    : `Maximum retention offers (${maxRetentionOffersPerAgreement}) have been applied to this agreement.`;
};

export interface RetentionOfferLimitData {
  retentionIconHidden: boolean;
  retentionLimitReached: boolean;
  retentionLimitTooltip: string;
}

// Single entry point for the Retention icon: combines the helpers above
// into the one shape the UI needs. The individual helpers stay exported
// for direct reuse/unit testing.
export const getRetentionOfferLimitData = (
  config: RetentionOfferLimitConfig,
  agreementActivities: string[] | null | undefined
): RetentionOfferLimitData => {
  const retentionOfferCount = getRetentionOfferCount(agreementActivities);

  return {
    retentionIconHidden: isRetentionIconHidden(config),
    retentionLimitReached: isRetentionLimitReached({
      ...config,
      retentionOfferCount,
    }),
    retentionLimitTooltip: getRetentionLimitTooltip(
      config.maxRetentionOffersPerAgreement
    ),
  };
};
